import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Package, LogOut, Plus, X, Search, Users,
  Check, XCircle, Star, AlertCircle, RotateCcw, Pencil, Save, Trash2, UserPlus,
  History, Database, Download, RefreshCw, ChevronLeft, ChevronRight, Shield,
  FileText, Upload, FileUp, CheckCircle, Minus, MoreVertical, BookOpen, ClipboardCheck
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { SmartSelect, BrandAutocomplete, AddTireForm as SharedAddTireForm } from "../components/TireFormShared";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Speed index order (technical order, not alphabetical)
const SPEED_INDICES = ["J", "K", "L", "M", "N", "P", "Q", "R", "S", "T", "U", "H", "V", "W", "Y"];

// Tab configuration
const TABS = [
  { id: "new_pairs", label: "Paires Neuves", view: "new_pairs" },
  { id: "used_pairs", label: "Paires Occasion", view: "used_pairs" },
  { id: "new_singles", label: "Seuls Neufs", view: "new_singles" },
  { id: "used_singles", label: "Seuls Occasion", view: "used_singles" },
  { id: "pending", label: "À Tester", view: "pending" },
];

// Condition score display as stars
const ConditionStars = ({ score }) => {
  if (!score) return null;
  return (
    <span className="inline-flex items-center text-[#0A0A0A]" data-testid="condition-stars">
      <span className="sm:hidden text-xs font-bold">{score}</span>
      <span className="hidden sm:inline-flex items-center">
        {[1, 2, 3, 4].map((i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${i <= score ? 'fill-current' : 'opacity-20'}`}
          />
        ))}
      </span>
    </span>
  );
};

// Single line tire row component with click handler
const TireRow = ({ group, onTest, onClick, isTestMode = false, categoryLabel, onExit, onDeleteDepleted, expandedKey, onExpand }) => {
  const [testAction, setTestAction] = useState(null);
  const [testScore, setTestScore] = useState(4);
  const [passedCount, setPassedCount] = useState(group.total_quantity);

  // Depleted placeholder (red line)
  if (group.is_depleted) {
    return (
      <div className="flex items-center justify-between py-2 px-3 bg-[#FEF2F2] border-b border-[#FECACA] text-sm" data-testid={`depleted-${group.group_key}`}>
        <div className="flex items-center">
          <span className="font-mono font-bold text-[#DC2626] whitespace-nowrap">
            {group.dimensions}
          </span>
          <span className="ml-3 text-xs text-[#DC2626] font-medium">Rupture de stock</span>
        </div>
        {onDeleteDepleted && (
          <button
            onClick={() => {
              if (window.confirm("Supprimer cette alerte de rupture ?")) {
                onDeleteDepleted(group);
              }
            }}
            className="ml-2 text-[#DC2626] hover:text-[#991B1B] hover:bg-[#FECACA] rounded p-1 transition-colors"
            data-testid={`delete-depleted-${group.group_key}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isExpanded = expandedKey === group.group_key;

  const handleRowClick = (e) => {
    if (e.target.closest('button') || e.target.closest('[role="combobox"]') || e.target.closest('[role="menu"]') || e.target.closest('[data-radix-collection-item]')) return;
    if (isMobile) {
      onExpand(isExpanded ? null : group.group_key);
    }
  };

  return (
    <div 
      className={`bg-white border-b border-[#E5E7EB] hover:bg-[#F3F4F6] transition-colors text-sm cursor-pointer group/row ${isExpanded ? 'bg-[#F9FAFB]' : ''}`}
      data-testid={`tire-row-${group.group_key}`}
      onClick={handleRowClick}
    >
      <div className="flex items-center justify-between py-2 px-3">
        {/* Main info - one line */}
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto no-scrollbar">
          <span className="font-mono font-bold text-[#0A0A0A] whitespace-nowrap">
            <span className="hidden sm:inline">{group.dimensions} {group.load_speed}</span>
            <span className="sm:hidden">{group.dimensions.replace(/(\d+)\s(\d+)\s(R)/, '$1/$2$3')} {group.load_speed}</span>
          </span>
          <span className="text-[#A3A3A3]">|</span>
          <span className="text-[#525252] whitespace-nowrap truncate">
            {group.brand} {group.model}
            {group.nv && <span className="inline-flex items-center ml-1 px-1.5 py-0 rounded text-[10px] font-bold bg-[#F97316] text-white leading-4">NV</span>}
          </span>
          {group.tire_type === "used" && group.condition_score && (
            <>
              <span className="text-[#A3A3A3]">|</span>
              <ConditionStars score={group.condition_score} />
            </>
          )}
          {categoryLabel && (
            <>
              <span className="text-[#A3A3A3]">|</span>
              <span className="text-xs px-1.5 py-0.5 bg-[#F3F4F6] text-[#525252] rounded whitespace-nowrap">
                <span className="hidden sm:inline">{categoryLabel}</span>
                <span className="sm:hidden">{
                  categoryLabel === "Paires Neuves" ? "N" :
                  categoryLabel === "Paires Occasion" ? "O" :
                  categoryLabel === "Seuls Neufs" ? "SN" :
                  categoryLabel === "Seuls Occasion" ? "SO" :
                  categoryLabel === "À Tester" ? "T" : categoryLabel
                }</span>
              </span>
            </>
          )}
        </div>

        {/* Quantity & Actions */}
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          <span className={`px-2 py-0.5 text-xs font-bold rounded-sm whitespace-nowrap ${
            group.is_pair ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#F3F4F6] text-[#525252]'
          }`}>
            ×{group.total_quantity}
          </span>

        {!isTestMode && onExit && (
          <>
            {/* Desktop: hover buttons */}
            <div className="hidden sm:flex items-center gap-1.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] hover:text-[#991B1B]"
                onClick={(e) => { e.stopPropagation(); onExit(group); }}
                data-testid={`exit-btn-${group.group_key}`}
              >
                <Minus className="w-3 h-3 mr-1" />
                Sortir
              </Button>
              <Pencil className="w-3.5 h-3.5 text-[#A3A3A3]" />
            </div>

            {/* Mobile: 3-dot menu */}
            <div className="sm:hidden" onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1.5 rounded-md hover:bg-[#F3F4F6] text-[#525252]"
                    data-testid={`menu-btn-${group.group_key}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onClick(group)}
                    data-testid={`menu-edit-${group.group_key}`}
                  >
                    <Pencil className="w-4 h-4 mr-2 text-[#525252]" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer text-[#DC2626] focus:text-[#DC2626]"
                    onClick={() => onExit(group)}
                    data-testid={`menu-exit-${group.group_key}`}
                  >
                    <Minus className="w-4 h-4 mr-2" />
                    Sortir du stock
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}

        {!isTestMode && !onExit && (
          <Pencil className="w-3.5 h-3.5 text-[#A3A3A3] hidden sm:block" />
        )}

        {isTestMode && (
          <>
            {!testAction && (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  className="h-7 px-2 text-xs bg-[#0A0A0A] text-white hover:bg-[#262626]"
                  onClick={() => {
                    setTestAction("validate");
                    setPassedCount(group.total_quantity);
                  }}
                  data-testid={`test-validate-${group.group_key}`}
                >
                  <Check className="w-3 h-3 mr-1" /> Tester
                </Button>
                <Button
                  size="sm"
                  className="h-7 px-2 text-xs bg-[#DC2626] text-white hover:bg-[#B91C1C]"
                  onClick={() => {
                    setTestAction("reject");
                    onTest({
                      group_key: group.group_key,
                      tires: group.tires,
                      total_quantity: group.total_quantity,
                      passed_count: 0,
                      condition_score: null
                    });
                  }}
                  data-testid={`test-reject-${group.group_key}`}
                >
                  <XCircle className="w-3 h-3" />
                </Button>
              </div>
            )}
            {testAction === "validate" && (
              <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-[#525252]">Passés:</span>
                  <Select value={passedCount.toString()} onValueChange={(v) => setPassedCount(parseInt(v))}>
                    <SelectTrigger className="w-14 h-7 text-xs" data-testid="test-passed-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: group.total_quantity + 1 }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>{i}/{group.total_quantity}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-[#525252]">Usure:</span>
                  <Select value={testScore.toString()} onValueChange={(v) => setTestScore(parseInt(v))}>
                    <SelectTrigger className="w-14 h-7 text-xs" data-testid="test-score-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs bg-[#0A0A0A] text-white"
                  onClick={() => onTest({
                    group_key: group.group_key,
                    tires: group.tires,
                    total_quantity: group.total_quantity,
                    passed_count: passedCount,
                    condition_score: testScore
                  })}
                  data-testid={`test-confirm-${group.group_key}`}
                >
                  Confirmer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-1"
                  onClick={() => setTestAction(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            {testAction === "reject" && (
              <span className="text-xs text-[#991B1B] font-medium">Tous rejetés</span>
            )}
          </>
        )}
      </div>
      </div>

      {/* Mobile expanded details */}
      {isExpanded && (
        <div className="sm:hidden px-3 pb-2 pt-1 border-t border-[#F3F4F6] bg-[#F9FAFB] text-xs space-y-1" data-testid={`expanded-${group.group_key}`}>
          <div className="flex justify-between">
            <span className="text-[#737373]">Marque / Modèle</span>
            <span className="font-medium text-[#0A0A0A]">{group.brand} {group.model}{group.nv ? ' [NV]' : ''}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#737373]">Dimension</span>
            <span className="font-mono font-medium">{group.dimensions} {group.load_speed}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#737373]">Quantité</span>
            <span className="font-medium">{group.total_quantity}</span>
          </div>
          {group.tire_type === "used" && group.condition_score && (
            <div className="flex justify-between items-center">
              <span className="text-[#737373]">Usure</span>
              <ConditionStars score={group.condition_score} />
            </div>
          )}
          {group.dot && (
            <div className="flex justify-between">
              <span className="text-[#737373]">DOT</span>
              <span className="font-medium">{group.dot}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Grouped tire list with diameter separators
const TireList = ({ groups, onTest, onClick, isTestMode, showCategoryLabel = false, onExit, onDeleteDepleted }) => {
  const [expandedKey, setExpandedKey] = useState(null);
  const { regularByDiameter, camionnetteByDiameter } = useMemo(() => {
    const regular = [];
    const camionnette = [];
    groups.forEach(group => {
      if (group.camionnette) {
        camionnette.push(group);
      } else {
        regular.push(group);
      }
    });

    const groupByDiam = (list) => {
      const byDiameter = {};
      list.forEach(group => {
        const d = group.diameter;
        if (!byDiameter[d]) byDiameter[d] = [];
        byDiameter[d].push(group);
      });
      return Object.entries(byDiameter)
        .sort(([a], [b]) => parseInt(b) - parseInt(a))
        .map(([diameter, items]) => ({ diameter: parseInt(diameter), items }));
    };

    return { regularByDiameter: groupByDiam(regular), camionnetteByDiameter: groupByDiam(camionnette) };
  }, [groups]);

  if (groups.length === 0) {
    return (
      <div className="text-center py-12" data-testid="empty-state">
        <Package className="w-12 h-12 text-[#A3A3A3] mx-auto mb-4" />
        <p className="text-[#525252]">Aucun pneu trouvé</p>
      </div>
    );
  }

  const renderSection = (groupedByDiameter, startIdx = 0) =>
    groupedByDiameter.map(({ diameter, items }, idx) => (
      <div key={diameter}>
        <div className={`bg-[#F3F4F6] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] ${(idx + startIdx) > 0 ? 'border-t-2 border-[#E5E7EB]' : ''}`}>
          — R{diameter} —
        </div>
        {items.map((group) => (
          <TireRow
            key={group.group_key}
            group={group}
            onTest={onTest}
            onClick={onClick}
            isTestMode={isTestMode && group.categoryId === "pending"}
            categoryLabel={showCategoryLabel ? group.categoryLabel : null}
            onExit={onExit}
            onDeleteDepleted={onDeleteDepleted}
            expandedKey={expandedKey}
            onExpand={setExpandedKey}
          />
        ))}
      </div>
    ));

  return (
    <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
      {renderSection(regularByDiameter)}
      {camionnetteByDiameter.length > 0 && (
        <>
          <div className="bg-[#0A0A0A] px-4 py-2 border-t-2 border-[#E5E7EB]" data-testid="camionnette-separator">
            <span className="text-xs font-bold uppercase tracking-widest text-white">Camionnette</span>
          </div>
          {renderSection(camionnetteByDiameter, regularByDiameter.length)}
        </>
      )}
    </div>
  );
};

// Smart input with dropdown and error state
// SmartSelect, BrandAutocomplete, AddTireForm imported from TireFormShared.js

// Wrap shared AddTireForm for DashboardPage usage (same API, just call onSuccess)
const AddTireForm = ({ onClose, onSuccess, formValues }) => {
  return <SharedAddTireForm onClose={onClose} onSuccess={onSuccess} formValues={formValues} />;
};

// Edit tire form (in modal)
const EditTireForm = ({ tire, onClose, onSuccess, formValues }) => {
  const tireData = tire.tires?.[0] || tire;
  const [formData, setFormData] = useState({
    width: tire.width?.toString() || "",
    height: tire.height?.toString() || "",
    diameter: tire.diameter?.toString() || "",
    load_index: tire.load_index?.toString() || "",
    speed_index: tire.speed_index || "",
    brand: tire.brand || "",
    model: tire.model || "",
    tire_type: tire.tire_type || "used",
    season: tireData.season || tire.season || "ete",
    runflat: tireData.runflat || tire.runflat || false,
    camionnette: tireData.camionnette || tire.camionnette || false,
    nv: tireData.nv || tire.nv || false,
    condition_score: tire.condition_score || null,
    dot: tire.dot || "",
    quantity: tire.total_quantity || 1,
    is_active: tire.is_active !== false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const widthOptions = [145, 155, 165, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295, 305, 315, 325, 335, 345, 355];

  const heightOptions = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85];

  const diameterOptions = [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

  const loadOptions = useMemo(() => {
    const base = [71, 75, 79, 82, 84, 86, 87, 88, 89, 91, 92, 94, 95, 97, 99, 100, 102, 104, 106, 108, 110, 112, 115, 116, 118, 120];
    return ["—", ...Array.from(new Set([...base, ...(formValues.load_indices || [])])).sort((a, b) => a - b)];
  }, [formValues.load_indices]);

  const validateForm = () => {
    const newErrors = {};
    const requiredMessage = "Ce champ est obligatoire";
    if (!formData.width) newErrors.width = requiredMessage;
    if (!formData.height) newErrors.height = requiredMessage;
    if (!formData.diameter) newErrors.diameter = requiredMessage;
    if (!formData.brand || formData.brand.trim() === "") newErrors.brand = requiredMessage;
    if (!formData.model || formData.model.trim() === "") newErrors.model = requiredMessage;
    if (!formData.quantity || formData.quantity < 1) newErrors.quantity = requiredMessage;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const tireId = tire.tires?.[0]?.id;
      if (!tireId) throw new Error("ID du pneu non trouvé");

      const data = {
        width: parseInt(formData.width),
        height: parseInt(formData.height),
        diameter: parseInt(formData.diameter),
        load_index: formData.load_index && formData.load_index !== "—" ? parseInt(formData.load_index) : null,
        speed_index: formData.speed_index && formData.speed_index !== "—" ? formData.speed_index : null,
        brand: formData.brand,
        model: formData.model,
        tire_type: formData.tire_type,
        season: formData.season,
        runflat: formData.runflat,
        camionnette: formData.camionnette,
        quantity: parseInt(formData.quantity),
        condition_score: formData.tire_type === "used" && formData.condition_score ? parseInt(formData.condition_score) : null,
        dot: formData.tire_type === "used" ? formData.dot : null,
        nv: formData.nv,
        is_active: formData.is_active
      };

      await axios.put(`${API_URL}/api/tires/${tireId}`, data, { withCredentials: true });
      toast.success("Pneu modifié avec succès");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce pneu ?")) return;
    setIsLoading(true);
    try {
      const tireId = tire.tires?.[0]?.id;
      if (!tireId) throw new Error("ID du pneu non trouvé");
      await axios.delete(`${API_URL}/api/tires/${tireId}`, { withCredentials: true });
      toast.success("Pneu supprimé");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-tire-form">
      <div className="bg-[#F3F4F6] p-3 rounded-sm mb-4">
        <div className="font-mono font-bold text-lg text-[#0A0A0A]">
          {formData.runflat ? "RF" : ""}{formData.camionnette ? "C" : ""}{formData.season === "hiver" ? "H" : formData.season === "4saisons" ? "Q" : ""}{formData.width} {formData.height} R{formData.diameter} {formData.load_index && formData.load_index !== "—" ? formData.load_index : ""}{formData.speed_index && formData.speed_index !== "—" ? formData.speed_index : ""}
        </div>
        <div className="text-sm text-[#525252]">{formData.brand} {formData.model}</div>
      </div>

      <div>
        <Label className="input-label">Type</Label>
        <div className="flex gap-2">
          <Button type="button" className={formData.tire_type === "used" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("tire_type", "used")}>Occasion</Button>
          <Button type="button" className={formData.tire_type === "new" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("tire_type", "new")}>Neuf</Button>
        </div>
      </div>

      {/* Season */}
      <div>
        <Label className="input-label">Saison</Label>
        <div className="flex gap-2">
          <Button type="button" className={formData.season === "ete" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "ete")}>Été</Button>
          <Button type="button" className={formData.season === "4saisons" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "4saisons")}>4 Saisons</Button>
          <Button type="button" className={formData.season === "hiver" ? "btn-primary flex-1" : "btn-secondary flex-1"} onClick={() => updateField("season", "hiver")}>Hiver</Button>
        </div>
      </div>

      {/* RunFlat & Camionnette */}
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={formData.runflat} onCheckedChange={(v) => updateField("runflat", !!v)} />
          <span className="text-sm text-[#0A0A0A]">RunFlat</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={formData.camionnette} onCheckedChange={(v) => updateField("camionnette", !!v)} />
          <span className="text-sm text-[#0A0A0A]">Camionnette</span>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SmartSelect value={formData.width} onChange={(v) => updateField("width", v)} options={widthOptions} placeholder="205" label="Largeur" testId="edit-width" error={errors.width} />
        <SmartSelect value={formData.height} onChange={(v) => updateField("height", v)} options={heightOptions} placeholder="55" label="Hauteur" testId="edit-height" error={errors.height} />
        <SmartSelect value={formData.diameter} onChange={(v) => updateField("diameter", v)} options={diameterOptions} placeholder="16" label="Diamètre" testId="edit-diameter" error={errors.diameter} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SmartSelect value={formData.load_index} onChange={(v) => updateField("load_index", v)} options={loadOptions} placeholder="91" label="Indice charge" testId="edit-load-index" error={errors.load_index} required={false} />
        <div>
          <Label className="input-label">Indice vitesse</Label>
          <Select value={formData.speed_index} onValueChange={(v) => updateField("speed_index", v)}>
            <SelectTrigger className="input-field" data-testid="edit-speed-index"><SelectValue placeholder="V" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="—">—</SelectItem>
              {SPEED_INDICES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <BrandAutocomplete value={formData.brand} onChange={(v) => updateField("brand", v)} suggestions={formValues.brands || []} error={errors.brand} />
        <div>
          <Label className={`input-label ${errors.model ? 'text-[#DC2626]' : ''}`}>Modèle *</Label>
          <Input placeholder="Primacy 4" value={formData.model} onChange={(e) => updateField("model", e.target.value)} className={`input-field ${errors.model ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`} />
          {errors.model && <p className="text-xs text-[#DC2626] mt-1">{errors.model}</p>}
        </div>
      </div>

      {formData.tire_type === "used" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="input-label">DOT</Label>
            <Input placeholder="2523" maxLength={10} value={formData.dot} onChange={(e) => updateField("dot", e.target.value)} className="input-field" />
          </div>
          <div>
            <Label className="input-label">Usure</Label>
            <Select value={formData.condition_score?.toString() || ""} onValueChange={(v) => updateField("condition_score", v ? parseInt(v) : null)}>
              <SelectTrigger className="input-field"><SelectValue placeholder="Non défini" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 - Excellent</SelectItem>
                <SelectItem value="3">3 - Bon</SelectItem>
                <SelectItem value="2">2 - Moyen</SelectItem>
                <SelectItem value="1">1 - Usé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-nv-checkbox">
        <Checkbox checked={formData.nv} onCheckedChange={(v) => updateField("nv", !!v)} />
        <span className="text-sm text-[#0A0A0A]">NV</span>
      </label>

      <div className="w-1/2">
        <Label className={`input-label ${errors.quantity ? 'text-[#DC2626]' : ''}`}>Quantité *</Label>
        <Input type="number" min="1" value={formData.quantity} onChange={(e) => updateField("quantity", e.target.value)} className={`input-field ${errors.quantity ? 'border-[#DC2626] ring-2 ring-[#DC2626]/20' : ''}`} />
        {errors.quantity && <p className="text-xs text-[#DC2626] mt-1">{errors.quantity}</p>}
      </div>

      <div className="flex gap-2 pt-4 border-t border-[#E5E7EB]" data-testid="edit-form-footer">
        <Button type="button" variant="outline" className="text-[#DC2626] border-[#DC2626] hover:bg-[#FEE2E2]" onClick={handleDelete} disabled={isLoading}>Supprimer</Button>
        <div className="flex-1" />
        <Button type="button" variant="outline" className="btn-secondary" onClick={onClose}>Annuler</Button>
        <Button type="submit" className="btn-primary" disabled={isLoading} data-testid="save-tire-btn"><Save className="w-4 h-4 mr-1" />{isLoading ? "..." : "Enregistrer"}</Button>
      </div>
    </form>
  );
};

// User Management Panel
const UserManagementPanel = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", role: "user" });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({ email: "", password: "", name: "", role: "user" });
    setErrors({});
    setShowAddUser(false);
    setEditingUser(null);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "********",
      name: user.name,
      role: user.role
    });
    setShowAddUser(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || !formData.email.includes("@")) newErrors.email = "Email invalide";
    if (!editingUser && (!formData.password || formData.password.length < 6)) newErrors.password = "Mot de passe requis (min. 6 caractères)";
    if (!formData.name || formData.name.trim() === "") newErrors.name = "Nom requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, formData, { withCredentials: true });
        toast.success("Utilisateur modifié");
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
        toast.success("Utilisateur créé");
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;

    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-4" data-testid="user-management-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#525252]">{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
        {!showAddUser && (
          <Button
            size="sm"
            className="h-8 px-3 bg-[#0A0A0A] text-white hover:bg-[#262626]"
            onClick={() => setShowAddUser(true)}
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        )}
      </div>

      {/* Add/Edit User Form */}
      {showAddUser && (
        <form onSubmit={handleSubmit} className="bg-[#F3F4F6] p-3 sm:p-4 rounded-md space-y-3" data-testid="user-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className={`input-label ${errors.name ? 'text-[#DC2626]' : ''}`}>Nom *</Label>
              <Input
                placeholder="Jean Dupont"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`input-field ${errors.name ? 'border-[#DC2626]' : ''}`}
                data-testid="user-name-input"
              />
              {errors.name && <p className="text-xs text-[#DC2626] mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label className="input-label">Rôle</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="input-field" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="user">Utilisateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className={`input-label ${errors.email ? 'text-[#DC2626]' : ''}`}>Email *</Label>
              <Input
                type="email"
                placeholder="email@exemple.fr"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`input-field ${errors.email ? 'border-[#DC2626]' : ''}`}
                data-testid="user-email-input"
              />
              {errors.email && <p className="text-xs text-[#DC2626] mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label className={`input-label ${errors.password ? 'text-[#DC2626]' : ''}`}>
                Mot de passe {editingUser ? "(laisser ******** pour ne pas changer)" : "*"}
              </Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={`input-field ${errors.password ? 'border-[#DC2626]' : ''}`}
                data-testid="user-password-input"
              />
              {errors.password && <p className="text-xs text-[#DC2626] mt-1">{errors.password}</p>}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={resetForm} className="h-8">
              Annuler
            </Button>
            <Button type="submit" size="sm" className="h-8 bg-[#0A0A0A] text-white" disabled={isSaving} data-testid="save-user-btn">
              <Save className="w-3 h-3 mr-1" />
              {isSaving ? "..." : editingUser ? "Modifier" : "Créer"}
            </Button>
          </div>
        </form>
      )}

      {/* User List */}
      {isLoading ? (
        <div className="text-center py-8 text-[#525252]">Chargement...</div>
      ) : (
        <div className="space-y-2">
          {users.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-2.5 sm:p-3 bg-white border border-[#E5E7EB] rounded-md gap-2"
              data-testid={`user-row-${user.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#0A0A0A] text-sm">{user.name}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] sm:text-xs font-medium rounded ${
                    user.role === "admin" ? "bg-[#FEF08A] text-[#854D0E]" : "bg-[#F3F4F6] text-[#525252]"
                  }`}>
                    {user.role === "admin" ? "Admin" : "Utilisateur"}
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-[#525252] truncate">{user.email}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                  onClick={() => openEditUser(user)}
                  data-testid={`edit-user-${user.id}`}
                >
                  <Pencil className="w-3.5 h-3.5 text-[#525252]" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#FEE2E2]"
                  onClick={() => handleDelete(user.id)}
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Activity Log Panel
const ActivityLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filterAction) params.action = filterAction;
      const response = await axios.get(`${API_URL}/api/logs`, { params, withCredentials: true });
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setIsLoading(false);
    }
  }, [page, filterAction]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const actionLabels = {
    tire_added: "Pneu ajouté",
    tire_modified: "Pneu modifié",
    tire_deleted: "Pneu supprimé",
    test_performed: "Test effectué",
    user_created: "Utilisateur créé",
    user_deleted: "Utilisateur supprimé",
    backup_created: "Sauvegarde créée",
    stock_exit: "Sortie stock",
    csv_imported: "Import CSV"
  };

  const actionColors = {
    tire_added: "bg-[#DCFCE7] text-[#166534]",
    tire_modified: "bg-[#DBEAFE] text-[#1E40AF]",
    tire_deleted: "bg-[#FEE2E2] text-[#991B1B]",
    test_performed: "bg-[#FEF08A] text-[#854D0E]",
    user_created: "bg-[#E0E7FF] text-[#3730A3]",
    user_deleted: "bg-[#FEE2E2] text-[#991B1B]",
    backup_created: "bg-[#F3E8FF] text-[#6B21A8]",
    stock_exit: "bg-[#FFF7ED] text-[#9A3412]",
    csv_imported: "bg-[#ECFDF5] text-[#065F46]"
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " +
           d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDetails = (action, details) => {
    if (!details) return "";
    if (action === "tire_added" || action === "tire_deleted") {
      return `${details.dimensions || ""} ${details.brand || ""} ${details.model || ""} ×${details.quantity || 1}`;
    }
    if (action === "tire_modified") {
      const changes = details.changes || [];
      return `${details.dimensions || ""} ${details.brand || ""} — ${changes.length} modification${changes.length > 1 ? "s" : ""}`;
    }
    if (action === "test_performed") {
      return `${details.dimensions || ""} ${details.brand || ""} — ${details.passed || 0} passé(s), ${details.rejected || 0} rejeté(s)`;
    }
    if (action === "user_created" || action === "user_deleted") {
      return `${details.target_user || ""} (${details.target_email || ""})`;
    }
    if (action === "backup_created") {
      return details.manual ? "Sauvegarde manuelle" : "Sauvegarde automatique";
    }
    if (action === "stock_exit") {
      return `${details.dimensions || ""} ${details.brand || ""} ${details.model || ""} — ${details.quantity_removed || 0} sorti(s) (${details.quantity_before || "?"}→${details.quantity_after || "?"})`;
    }
    if (action === "csv_imported") {
      return `${details.filename || "fichier"} — ${details.imported || 0} importé(s), ${details.errors || 0} erreur(s)`;
    }
    return JSON.stringify(details);
  };

  const ACTION_FILTER_OPTIONS = [
    { value: "", label: "Toutes les actions" },
    { value: "tire_added", label: "Pneu ajouté" },
    { value: "tire_modified", label: "Pneu modifié" },
    { value: "tire_deleted", label: "Pneu supprimé" },
    { value: "test_performed", label: "Test effectué" },
    { value: "stock_exit", label: "Sortie stock" },
    { value: "csv_imported", label: "Import CSV" },
    { value: "user_created", label: "Utilisateur créé" },
    { value: "user_deleted", label: "Utilisateur supprimé" },
    { value: "backup_created", label: "Sauvegarde créée" }
  ];

  return (
    <div className="space-y-4" data-testid="activity-log-panel">
      {/* Filters */}
      <div className="flex items-center justify-between gap-2">
        <Select value={filterAction || "all"} onValueChange={(v) => { setFilterAction(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[140px] sm:w-[200px] h-8 text-xs sm:text-sm" data-testid="log-filter-action">
            <SelectValue placeholder="Toutes les actions" />
          </SelectTrigger>
          <SelectContent>
            {ACTION_FILTER_OPTIONS.map(opt => (
              <SelectItem key={opt.value || "all"} value={opt.value || "all"}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#525252]">{total} entrée{total > 1 ? "s" : ""}</span>
      </div>

      {/* Log List */}
      {isLoading ? (
        <div className="text-center py-8 text-[#525252]">Chargement...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8" data-testid="no-logs">
          <History className="w-10 h-10 text-[#A3A3A3] mx-auto mb-3" />
          <p className="text-[#525252]">Aucune activité enregistrée</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {logs.map((log, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 p-2 sm:p-2.5 bg-white border border-[#E5E7EB] rounded-md text-sm" data-testid={`log-entry-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded whitespace-nowrap ${actionColors[log.action] || "bg-[#F3F4F6] text-[#525252]"}`}>
                  {actionLabels[log.action] || log.action}
                </span>
                <span className="text-[10px] sm:text-xs text-[#A3A3A3] sm:hidden">
                  {formatTimestamp(log.timestamp)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#0A0A0A] text-xs sm:text-sm break-words">{formatDetails(log.action, log.details)}</p>
                <p className="text-[10px] sm:text-xs text-[#A3A3A3] mt-0.5 hidden sm:block">
                  {log.user_name} — {formatTimestamp(log.timestamp)}
                </p>
                <p className="text-[10px] text-[#A3A3A3] sm:hidden">
                  {log.user_name}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline" size="sm" className="h-8 w-8 p-0"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            data-testid="logs-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#525252]">{page} / {pages}</span>
          <Button
            variant="outline" size="sm" className="h-8 w-8 p-0"
            disabled={page >= pages}
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            data-testid="logs-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Backup Management Panel
const BackupPanel = () => {
  const [backups, setBackups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const fetchBackups = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/backups`, { withCredentials: true });
      setBackups(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des sauvegardes");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreateBackup = async () => {
    setIsCreating(true);
    try {
      await axios.post(`${API_URL}/api/backups`, {}, { withCredentials: true });
      toast.success("Sauvegarde créée avec succès");
      fetchBackups();
    } catch (error) {
      toast.error("Erreur lors de la création de la sauvegarde");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (backupId) => {
    try {
      const response = await axios.get(`${API_URL}/api/backups/${backupId}/download`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1]?.replace(/"/g, "") : `sauvegarde_${backupId}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) + " à " +
           d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-4" data-testid="backup-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <p className="text-xs sm:text-sm text-[#525252]">
          Sauvegarde automatique le 1er de chaque mois.
        </p>
        <Button
          size="sm"
          className="h-8 px-3 bg-[#0A0A0A] text-white hover:bg-[#262626] self-start sm:self-auto"
          onClick={handleCreateBackup}
          disabled={isCreating}
          data-testid="create-backup-btn"
        >
          <Database className="w-4 h-4 mr-1" />
          {isCreating ? "Création..." : "Sauvegarder"}
        </Button>
      </div>

      {/* Backup List */}
      {isLoading ? (
        <div className="text-center py-8 text-[#525252]">Chargement...</div>
      ) : backups.length === 0 ? (
        <div className="text-center py-8" data-testid="no-backups">
          <Database className="w-10 h-10 text-[#A3A3A3] mx-auto mb-3" />
          <p className="text-[#525252]">Aucune sauvegarde disponible</p>
        </div>
      ) : (
        <div className="space-y-2">
          {backups.map((backup) => (
            <div
              key={backup.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 sm:p-3 bg-white border border-[#E5E7EB] rounded-md"
              data-testid={`backup-row-${backup.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#0A0A0A] text-xs sm:text-sm">{formatDate(backup.created_at)}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] sm:text-xs rounded ${
                    backup.manual ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#F3E8FF] text-[#6B21A8]"
                  }`}>
                    {backup.manual ? "Manuelle" : "Auto"}
                  </span>
                </div>
                <div className="text-[10px] sm:text-xs text-[#525252] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{backup.stats?.entries_count || 0} entrées</span>
                  <span>{backup.stats?.total_tires || 0} pneus</span>
                  <span>{backup.stats?.new_count || 0} neufs</span>
                  <span>{backup.stats?.used_count || 0} occ.</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 px-3 self-start sm:self-auto text-xs"
                onClick={() => handleDownload(backup.id)}
                data-testid={`download-backup-${backup.id}`}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                CSV
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// CSV Import Panel
const CsvImportPanel = ({ onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        toast.error("Veuillez sélectionner un fichier CSV");
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await axios.post(`${API_URL}/api/import/csv`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} pneu(s) importé(s)`);
        if (onImportSuccess) onImportSuccess();
      }
      if (response.data.errors_count > 0) {
        toast.error(`${response.data.errors_count} erreur(s) détectée(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setIsUploading(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4" data-testid="csv-import-panel">
      {/* Instructions */}
      <div className="bg-[#F3F4F6] p-3 rounded-md text-xs sm:text-sm text-[#525252] space-y-2">
        <p className="font-medium text-[#0A0A0A]">Format CSV attendu :</p>
        <p>Même format que l'export CSV (séparateur point-virgule, en-têtes français).</p>
        <p className="font-mono text-[10px] sm:text-xs bg-white p-2 rounded border border-[#E5E7EB] overflow-x-auto whitespace-nowrap">
          Largeur;Hauteur;Diamètre;Indice charge;Indice vitesse;Marque;Modèle;Type;Saison;RunFlat;Camionnette;Usure;DOT;Quantité;Testé;Actif
        </p>
        <p className="text-xs">
          <strong>Type</strong> : "Neuf" ou "Occasion" — <strong>Usure</strong> : 1 à 4 (ou "-") — <strong>Testé</strong> : "Oui" ou "Non"
        </p>
      </div>

      {/* File Selection */}
      {!result && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="csv-file-input"
            />
            <Button
              variant="outline"
              className="h-10 px-4"
              onClick={() => fileInputRef.current?.click()}
              data-testid="select-csv-btn"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Sélectionner un fichier CSV
            </Button>
            {selectedFile && (
              <span className="text-sm text-[#525252]">{selectedFile.name}</span>
            )}
          </div>

          {selectedFile && (
            <div className="flex gap-2">
              <Button
                className="h-9 px-4 bg-[#0A0A0A] text-white hover:bg-[#262626]"
                onClick={handleImport}
                disabled={isUploading}
                data-testid="start-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? "Import en cours..." : "Lancer l'import"}
              </Button>
              <Button variant="outline" className="h-9" onClick={resetImport}>
                Annuler
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Import Results */}
      {result && (
        <div className="space-y-3" data-testid="import-results">
          {/* Summary */}
          <div className="flex gap-3">
            {result.imported > 0 && (
              <div className="flex-1 bg-[#DCFCE7] border border-[#BBF7D0] rounded-md p-3 text-center">
                <CheckCircle className="w-6 h-6 text-[#166534] mx-auto mb-1" />
                <div className="text-2xl font-bold text-[#166534]">{result.imported}</div>
                <div className="text-xs text-[#166534]">importé{result.imported > 1 ? "s" : ""}</div>
              </div>
            )}
            {result.errors_count > 0 && (
              <div className="flex-1 bg-[#FEE2E2] border border-[#FECACA] rounded-md p-3 text-center">
                <AlertCircle className="w-6 h-6 text-[#991B1B] mx-auto mb-1" />
                <div className="text-2xl font-bold text-[#991B1B]">{result.errors_count}</div>
                <div className="text-xs text-[#991B1B]">erreur{result.errors_count > 1 ? "s" : ""}</div>
              </div>
            )}
          </div>

          {/* Error Details */}
          {result.errors && result.errors.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-[#991B1B]">Détail des erreurs :</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {result.errors.map((err, i) => (
                  <div key={i} className="text-xs bg-[#FEF2F2] p-2 rounded border border-[#FECACA]">
                    <span className="font-mono font-bold">Ligne {err.line}</span> : {err.error}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button variant="outline" onClick={resetImport} data-testid="new-import-btn">
            Nouvel import
          </Button>
        </div>
      )}
    </div>
  );
};

// Inventory report tire list section
const InvTireListSection = ({ title, tires, color }) => (
  tires && tires.length > 0 ? (
    <div className="mb-3">
      <div className={`text-xs font-bold uppercase tracking-wider ${color} mb-1`}>{title} ({tires.length})</div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {tires.map((t, i) => (
          <div key={i} className="text-xs bg-[#F9FAFB] rounded px-2 py-1 flex items-center justify-between">
            <span className="font-mono font-bold">{t.width}/{t.height}R{t.diameter}</span>
            <span className="text-[#525252]">{t.brand} {t.model}</span>
            <span className="text-[#A3A3A3]">x{t.quantity}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null
);

// Inventory History Panel
const InventoryHistoryPanel = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filterUser, setFilterUser] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filterUser) params.user_name = filterUser;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await axios.get(`${API_URL}/api/inventory/history`, { params, withCredentials: true });
      setReports(res.data);
    } catch (e) { console.error("Error fetching inventory history:", e); }
    finally { setIsLoading(false); }
  }, [filterUser, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const openDetail = async (reportId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/inventory/history/${reportId}`, { withCredentials: true });
      setSelectedReport(res.data);
    } catch (e) { toast.error("Erreur chargement détail"); }
    finally { setDetailLoading(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filterLabels = (f) => {
    if (!f) return "—";
    const parts = [];
    if (f.tire_type === "new") parts.push("Neuf");
    else if (f.tire_type === "used") parts.push("Occasion");
    else parts.push("Tous");
    if (f.category === "pairs") parts.push("Paires");
    else if (f.category === "singles") parts.push("Seuls");
    if (f.camionnette === true) parts.push("Camionnette");
    else if (f.diameter) parts.push(`R${f.diameter}`);
    return parts.join(" · ") || "Tous";
  };

  if (selectedReport) {
    return (
      <div data-testid="inventory-detail">
        <Button variant="outline" size="sm" onClick={() => setSelectedReport(null)} className="mb-3 h-7 text-xs" data-testid="back-to-list-btn">
          <ChevronLeft className="w-3 h-3 mr-1" /> Retour à la liste
        </Button>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-sm">
          <div className="bg-[#F9FAFB] rounded p-2"><span className="text-[#A3A3A3] text-xs block">Début</span><span className="font-mono text-xs">{formatDate(selectedReport.started_at)}</span></div>
          <div className="bg-[#F9FAFB] rounded p-2"><span className="text-[#A3A3A3] text-xs block">Fin</span><span className="font-mono text-xs">{formatDate(selectedReport.completed_at)}</span></div>
          <div className="bg-[#F9FAFB] rounded p-2"><span className="text-[#A3A3A3] text-xs block">Utilisateur</span><span className="text-xs font-medium">{selectedReport.user_name}</span></div>
          <div className="bg-[#F9FAFB] rounded p-2"><span className="text-[#A3A3A3] text-xs block">Filtres</span><span className="text-xs font-medium">{filterLabels(selectedReport.filters)}</span></div>
        </div>
        <div className="grid grid-cols-5 gap-2 mb-4 text-center">
          <div className="bg-white border border-[#E5E7EB] rounded p-2"><div className="font-mono font-bold text-lg">{selectedReport.total_audited}</div><div className="text-[10px] text-[#A3A3A3] uppercase">Audités</div></div>
          <div className="bg-white border border-[#D1FAE5] rounded p-2"><div className="font-mono font-bold text-lg text-[#16A34A]">{selectedReport.total_present}</div><div className="text-[10px] text-[#A3A3A3] uppercase">Présents</div></div>
          <div className="bg-white border border-[#FECACA] rounded p-2"><div className="font-mono font-bold text-lg text-[#DC2626]">{selectedReport.total_missing}</div><div className="text-[10px] text-[#A3A3A3] uppercase">Manquants</div></div>
          <div className="bg-white border border-[#FEF3C7] rounded p-2"><div className="font-mono font-bold text-lg text-[#854D0E]">{selectedReport.total_trashed}</div><div className="text-[10px] text-[#A3A3A3] uppercase">Corbeille</div></div>
          <div className="bg-white border border-[#DBEAFE] rounded p-2"><div className="font-mono font-bold text-lg text-[#2563EB]">{selectedReport.total_added || 0}</div><div className="text-[10px] text-[#A3A3A3] uppercase">Ajoutés</div></div>
        </div>
        <InvTireListSection title="Pneus confirmés présents" tires={selectedReport.checked_tires} color="text-[#16A34A]" />
        <InvTireListSection title="Pneus manquants" tires={selectedReport.missing_tires} color="text-[#DC2626]" />
        <InvTireListSection title="Pneus envoyés en corbeille" tires={selectedReport.trashed_tires} color="text-[#854D0E]" />
        <InvTireListSection title="Pneus ajoutés pendant la session" tires={selectedReport.added_tires} color="text-[#2563EB]" />
      </div>
    );
  }

  return (
    <div data-testid="inventory-history-panel">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <Label className="input-label text-[10px]">Utilisateur</Label>
          <Input className="input-field h-8 text-xs w-[130px]" placeholder="Nom..." value={filterUser} onChange={e => setFilterUser(e.target.value)} data-testid="inv-hist-filter-user" />
        </div>
        <div>
          <Label className="input-label text-[10px]">Du</Label>
          <Input type="date" className="input-field h-8 text-xs w-[130px]" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} data-testid="inv-hist-filter-from" />
        </div>
        <div>
          <Label className="input-label text-[10px]">Au</Label>
          <Input type="date" className="input-field h-8 text-xs w-[130px]" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} data-testid="inv-hist-filter-to" />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-8 text-[#A3A3A3] text-sm">Chargement...</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-8 text-[#A3A3A3] text-sm" data-testid="no-inventory-history">Aucun inventaire enregistré</div>
      ) : (
        <div className="space-y-1">
          {reports.map(r => (
            <button
              key={r.id}
              onClick={() => openDetail(r.id)}
              className="w-full text-left bg-white border border-[#E5E7EB] rounded p-2 hover:bg-[#F3F4F6] transition-colors"
              data-testid={`inv-report-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-[#A3A3A3]">{formatDate(r.completed_at)}</span>
                  <span className="text-xs font-medium text-[#0A0A0A]">{r.user_name}</span>
                  <span className="text-[10px] bg-[#F3F4F6] rounded px-1.5 py-0.5 text-[#525252]">{filterLabels(r.filters)}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-mono">{r.total_audited} audités</span>
                  <span className="font-mono text-[#16A34A]">{r.total_present} OK</span>
                  <span className="font-mono text-[#DC2626]">{r.total_missing} manq.</span>
                  {(r.total_added || 0) > 0 && <span className="font-mono text-[#2563EB]">+{r.total_added}</span>}
                  <ChevronRight className="w-3.5 h-3.5 text-[#A3A3A3]" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {detailLoading && <div className="text-center py-4 text-[#A3A3A3] text-sm">Chargement du détail...</div>}
    </div>
  );
};

// Admin Panel (combines Users, Logs, Backups, Import)
const AdminPanel = ({ onClose, initialTab = "users", onImportSuccess }) => {
  const [activeAdminTab, setActiveAdminTab] = useState(initialTab);

  const adminTabs = [
    { id: "users", label: "Utilisateurs", shortLabel: "Users", icon: Users },
    { id: "logs", label: "Historique", shortLabel: "Logs", icon: History },
    { id: "inventory", label: "Inventaires", shortLabel: "Inv.", icon: ClipboardCheck },
    { id: "backups", label: "Sauvegardes", shortLabel: "Backup", icon: Database },
    { id: "import", label: "Import CSV", shortLabel: "Import", icon: Upload }
  ];

  return (
    <div data-testid="admin-panel">
      {/* Tab navigation */}
      <div className="flex overflow-x-auto border-b border-[#E5E7EB] mb-4 -mt-2 no-scrollbar">
        {adminTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveAdminTab(tab.id)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeAdminTab === tab.id
                  ? "text-[#0A0A0A] border-[#0A0A0A]"
                  : "text-[#525252] border-transparent hover:text-[#0A0A0A]"
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel || tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeAdminTab === "users" && <UserManagementPanel onClose={onClose} />}
      {activeAdminTab === "logs" && <ActivityLogPanel />}
      {activeAdminTab === "inventory" && <InventoryHistoryPanel />}
      {activeAdminTab === "backups" && <BackupPanel />}
      {activeAdminTab === "import" && <CsvImportPanel onImportSuccess={onImportSuccess} />}
    </div>
  );
};

// Main Dashboard
const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("new_pairs");
  const [tabTires, setTabTires] = useState([]);
  const [stats, setStats] = useState({ new_tires: 0, used_tested: 0, pending: 0, total_quantity: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTire, setEditingTire] = useState(null);
  const [formValues, setFormValues] = useState({ widths: [], heights: [], diameters: [], load_indices: [], brands: [] });
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [adminInitialTab, setAdminInitialTab] = useState("users");
  
  // Search mode
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchWidth, setSearchWidth] = useState("");
  const [searchHeight, setSearchHeight] = useState("");
  const [searchDiameter, setSearchDiameter] = useState("");
  const [searchSeason, setSearchSeason] = useState("");
  const [searchRunflat, setSearchRunflat] = useState(false);
  const [searchCamionnette, setSearchCamionnette] = useState(false);
  const [allSearchTires, setAllSearchTires] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(
    TABS.reduce((acc, tab) => ({ ...acc, [tab.id]: tab.id === "new_pairs" || tab.id === "used_pairs" }), {})
  );

  const fetchFormValues = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tires/form-values`, { withCredentials: true });
      setFormValues(response.data);
    } catch (error) {
      console.error("Error fetching form values:", error);
    }
  }, []);

  // Fetch tires for current tab
  const fetchTabTires = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/tires/grouped`, {
        params: { view: activeTab },
        withCredentials: true
      });
      setTabTires(response.data.map(t => ({ ...t, categoryId: activeTab })));
    } catch (error) {
      console.error("Error fetching tires:", error);
      toast.error("Erreur lors du chargement des pneus");
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  // Fetch all tires for search mode
  // Fetch ALL tires once when search opens (raw data, no filtering)
  const fetchAllSearchTires = useCallback(async () => {
    setIsLoading(true);
    try {
      const responses = await Promise.all(
        TABS.map(tab =>
          axios.get(`${API_URL}/api/tires/grouped`, {
            params: { view: tab.view },
            withCredentials: true
          }).then(res => ({ category: tab, data: res.data }))
        )
      );

      const all = [];
      responses.forEach(({ category, data }) => {
        data.forEach(tire => {
          all.push({ ...tire, categoryId: category.id, categoryLabel: category.label });
        });
      });
      setAllSearchTires(all);
    } catch (error) {
      console.error("Error fetching search tires:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Apply client-side filters to allSearchTires (pure computation, no API calls)
  const filteredSearchResults = useMemo(() => {
    if (!isSearchMode || allSearchTires.length === 0) return [];
    let filtered = [...allSearchTires];

    // Category filter
    filtered = filtered.filter(t => selectedCategories[t.categoryId]);

    // Dimension filters (parsed as integers for robust comparison)
    if (searchWidth) {
      const w = parseInt(searchWidth, 10);
      if (!isNaN(w)) filtered = filtered.filter(t => parseInt(t.width, 10) === w);
    }
    if (searchHeight) {
      const h = parseInt(searchHeight, 10);
      if (!isNaN(h)) filtered = filtered.filter(t => parseInt(t.height, 10) === h);
    }
    if (searchDiameter) {
      const d = parseInt(searchDiameter, 10);
      if (!isNaN(d)) filtered = filtered.filter(t => parseInt(t.diameter, 10) === d);
    }
    // Season filter
    if (searchSeason) {
      filtered = filtered.filter(t => (t.season || t.tires?.[0]?.season || "ete") === searchSeason);
    }
    // RunFlat filter
    if (searchRunflat) {
      filtered = filtered.filter(t => t.runflat || t.tires?.[0]?.runflat);
    }
    // Camionnette filter
    if (searchCamionnette) {
      filtered = filtered.filter(t => t.camionnette || t.tires?.[0]?.camionnette);
    }

    // Sort
    filtered.sort((a, b) => {
      if (b.diameter !== a.diameter) return b.diameter - a.diameter;
      if (b.width !== a.width) return b.width - a.width;
      return b.height - a.height;
    });

    return filtered;
  }, [isSearchMode, allSearchTires, selectedCategories, searchWidth, searchHeight, searchDiameter, searchSeason, searchRunflat, searchCamionnette]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    if (isSearchMode) {
      fetchAllSearchTires();
    } else {
      fetchTabTires();
    }
    fetchStats();
    fetchFormValues();
  }, [isSearchMode, fetchTabTires, fetchAllSearchTires, fetchStats, fetchFormValues]);

  // Open search mode
  const openSearchMode = () => {
    setIsSearchMode(true);
    setSearchWidth("");
    setSearchHeight("");
    setSearchDiameter("");
    setSearchSeason("");
    setSearchRunflat(false);
    setSearchCamionnette(false);
    setSelectedCategories(TABS.reduce((acc, tab) => ({ ...acc, [tab.id]: tab.id === "new_pairs" || tab.id === "used_pairs" }), {}));
  };

  // Close search mode
  const closeSearchMode = () => {
    setIsSearchMode(false);
    setSearchWidth("");
    setSearchHeight("");
    setSearchDiameter("");
    setSearchSeason("");
    setSearchRunflat(false);
    setSearchCamionnette(false);
  };

  // Reset search filters
  const resetSearch = () => {
    setSearchWidth("");
    setSearchHeight("");
    setSearchDiameter("");
    setSearchSeason("");
    setSearchRunflat(false);
    setSearchCamionnette(false);
    setSelectedCategories(TABS.reduce((acc, tab) => ({ ...acc, [tab.id]: tab.id === "new_pairs" || tab.id === "used_pairs" }), {}));
  };

  // Toggle category
  const toggleCategory = (catId) => {
    setSelectedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Smart test handler
  const handleTest = async (testData) => {
    const { tires, total_quantity, passed_count, condition_score } = testData;
    
    try {
      await axios.post(`${API_URL}/api/tires/smart-test`, {
        tire_ids: tires.map(t => t.id),
        total_quantity,
        passed_count,
        condition_score
      }, { withCredentials: true });
      
      if (passed_count === 0) {
        toast.success(`${total_quantity} pneu(s) rejeté(s) et supprimé(s)`);
      } else {
        const pairs = Math.floor(passed_count / 2);
        const singles = passed_count % 2;
        const rejected = total_quantity - passed_count;
        
        let message = "";
        if (pairs > 0) message += `${pairs * 2} → Paires. `;
        if (singles > 0) message += `${singles} → Seuls. `;
        if (rejected > 0) message += `${rejected} rejeté(s).`;
        
        toast.success(`Test: ${message}`);
      }
      
      if (isSearchMode) {
        fetchAllSearchTires();
      } else {
        fetchTabTires();
      }
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du test");
    }
  };

  const handleTireClick = (tire) => {
    if (tire.is_depleted) return;
    setEditingTire(tire);
  };

  const refreshData = () => {
    if (isSearchMode) {
      fetchAllSearchTires();
    } else {
      fetchTabTires();
    }
    fetchStats();
    fetchFormValues();
  };

  const hasSearchFilters = searchWidth || searchHeight || searchDiameter || searchSeason || searchRunflat || searchCamionnette || Object.values(selectedCategories).some(v => !v);

  // Stock Exit Dialog state
  const [exitTire, setExitTire] = useState(null);
  const [exitQty, setExitQty] = useState(1);
  const [isExiting, setIsExiting] = useState(false);

  const handleStockExit = async () => {
    if (!exitTire) return;
    const tire = exitTire.tires?.[0];
    if (!tire) return;
    setIsExiting(true);
    try {
      await axios.post(`${API_URL}/api/tires/${tire.id}/exit`, { quantity: exitQty }, { withCredentials: true });
      toast.success(`${exitQty} pneu(s) sorti(s) du stock`);
      setExitTire(null);
      setExitQty(1);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sortie du stock");
    } finally {
      setIsExiting(false);
    }
  };

  const openExitDialog = (group) => {
    setExitTire(group);
    setExitQty(1);
  };

  const handleDeleteDepleted = async (group) => {
    try {
      const parts = group.group_key.replace("depleted-", "").split("-");
      const [w, h, d, ...catParts] = parts;
      const category = catParts.join("_");
      await axios.delete(`${API_URL}/api/depleted/${w}/${h}/${d}/${category}`, { withCredentials: true });
      toast.success("Alerte de rupture supprimée");
      fetchTabTires();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfCategories, setPdfCategories] = useState({
    new_pairs: false, used_pairs: false, new_singles: false, used_singles: false, pending: false,
    depleted_new: false, depleted_used: false
  });

  const pdfOptions = [
    { id: "new_pairs", label: "Paires Neuves" },
    { id: "used_pairs", label: "Paires Occasions" },
    { id: "new_singles", label: "Seuls Neufs" },
    { id: "used_singles", label: "Seuls Occasions" },
    { id: "pending", label: "À Tester" },
    { id: "depleted_new", label: "Rupture de stock — Neuves" },
    { id: "depleted_used", label: "Rupture de stock — Occasions" },
  ];

  const allPdfChecked = pdfOptions.every(o => pdfCategories[o.id]);

  const handleExportPDF = async () => {
    const selected = Object.entries(pdfCategories).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins une catégorie");
      return;
    }
    try {
      toast.info("Génération du PDF en cours...");
      const response = await axios.get(`${API_URL}/api/export/pdf`, {
        params: { categories: selected.join(",") },
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1]?.replace(/"/g, "") : "inventaire_recostock.pdf";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exporté avec succès");
      setShowPdfModal(false);
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0A0A0A] rounded-sm flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-[#0A0A0A] hidden sm:block">Reco Stock</span>
            </div>

            <div className="flex items-center gap-3">
              {/* Search button */}
              {!isSearchMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openSearchMode}
                  className="h-8 px-3"
                  data-testid="open-search-btn"
                >
                  <Search className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Rechercher</span>
                </Button>
              )}

              {/* PDF Export button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPdfModal(true)}
                className="h-8 px-3"
                data-testid="export-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
              
              <Button
                className="btn-primary h-8 px-3"
                onClick={() => setShowAddForm(true)}
                data-testid="add-tire-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Ajouter</span>
              </Button>

              {/* Compta Stock navigation */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/compta")}
                className="h-8 px-3 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/5"
                data-testid="compta-stock-btn"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Compta</span>
              </Button>

              {/* Inventaire navigation */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/inventaire")}
                className="h-8 px-3 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/5"
                data-testid="inventaire-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Inventaire</span>
              </Button>

              {/* Admin button - only for admin users */}
              {user?.role === "admin" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setAdminInitialTab("users"); setShowUserManagement(true); }}
                  className="h-8 px-3"
                  data-testid="admin-panel-btn"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              )}

              <span className="text-sm text-[#525252] hidden sm:block">{user?.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-[#525252] hover:text-[#0A0A0A] h-8 w-8 p-0"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-[#0A0A0A]" data-testid="stat-total">{stats.total_quantity}</span>
              <span className="text-[#A3A3A3]">total</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-[#166534]" data-testid="stat-new">{stats.new_tires}</span>
              <span className="text-[#A3A3A3]">neufs</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-[#374151]" data-testid="stat-used">{stats.used_tested}</span>
              <span className="text-[#A3A3A3]">occasion</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-[#854D0E]" data-testid="stat-pending">{stats.pending}</span>
              <span className="text-[#A3A3A3]">à tester</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Search Panel (overlay mode) */}
        {isSearchMode && (
          <div className="bg-white border border-[#E5E7EB] rounded-md p-4 mb-4 animate-slide-up" data-testid="search-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0A0A0A]">Recherche avancée</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeSearchMode}
                className="h-8 w-8 p-0"
                data-testid="close-search-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Dimension search fields */}
            <div className="flex flex-wrap gap-3 mb-3">
              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-[#525252] mb-1 block">Largeur</Label>
                <Input
                  type="number"
                  placeholder="205"
                  value={searchWidth}
                  onChange={(e) => setSearchWidth(e.target.value)}
                  className="input-field h-9"
                  data-testid="search-width"
                />
              </div>
              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-[#525252] mb-1 block">Hauteur</Label>
                <Input
                  type="number"
                  placeholder="55"
                  value={searchHeight}
                  onChange={(e) => setSearchHeight(e.target.value)}
                  className="input-field h-9"
                  data-testid="search-height"
                />
              </div>
              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-[#525252] mb-1 block">Diamètre</Label>
                <Input
                  type="number"
                  placeholder="16"
                  value={searchDiameter}
                  onChange={(e) => setSearchDiameter(e.target.value)}
                  className="input-field h-9"
                  data-testid="search-diameter"
                />
              </div>
              <div className="flex-1 min-w-[100px]">
                <Label className="text-xs text-[#525252] mb-1 block">Saison</Label>
                <Select value={searchSeason || "all"} onValueChange={(v) => setSearchSeason(v === "all" ? "" : v)}>
                  <SelectTrigger className="input-field h-9" data-testid="search-season">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="ete">Été</SelectItem>
                    <SelectItem value="4saisons">4 Saisons</SelectItem>
                    <SelectItem value="hiver">Hiver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                {hasSearchFilters && (
                  <Button
                    variant="outline"
                    className="h-9 px-3 text-sm"
                    onClick={resetSearch}
                    data-testid="reset-search-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Réinitialiser
                  </Button>
                )}
              </div>
            </div>

            {/* Specificity filters + Category checkboxes */}
            <div className="flex flex-wrap gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="search-runflat">
                <Checkbox
                  checked={searchRunflat}
                  onCheckedChange={(v) => setSearchRunflat(!!v)}
                  className="data-[state=checked]:bg-[#0A0A0A] data-[state=checked]:border-[#0A0A0A]"
                />
                <span className={searchRunflat ? "text-[#0A0A0A] font-medium" : "text-[#525252]"}>RunFlat</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="search-camionnette">
                <Checkbox
                  checked={searchCamionnette}
                  onCheckedChange={(v) => setSearchCamionnette(!!v)}
                  className="data-[state=checked]:bg-[#0A0A0A] data-[state=checked]:border-[#0A0A0A]"
                />
                <span className={searchCamionnette ? "text-[#0A0A0A] font-medium" : "text-[#525252]"}>Camionnette</span>
              </label>
              <span className="text-[#D4D4D4]">|</span>
              {TABS.map((tab) => (
                <label 
                  key={tab.id} 
                  className="flex items-center gap-2 cursor-pointer text-sm"
                  data-testid={`category-checkbox-${tab.id}`}
                >
                  <Checkbox
                    checked={selectedCategories[tab.id]}
                    onCheckedChange={() => toggleCategory(tab.id)}
                    className="data-[state=checked]:bg-[#0A0A0A] data-[state=checked]:border-[#0A0A0A]"
                  />
                  <span className={selectedCategories[tab.id] ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}>
                    {tab.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation (hidden in search mode) */}
        {!isSearchMode && (
          <div className="flex w-full overflow-x-auto border-b border-[#E5E7EB] mb-4 no-scrollbar">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'text-[#0A0A0A] border-[#0A0A0A]' 
                    : 'text-[#525252] border-transparent hover:text-[#0A0A0A]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Tire List */}
        {isLoading ? (
          <div className="text-center py-12 text-[#525252]">
            <div className="animate-pulse">Chargement...</div>
          </div>
        ) : (
          <TireList 
            groups={isSearchMode ? filteredSearchResults : tabTires} 
            onTest={handleTest}
            onClick={handleTireClick}
            isTestMode={isSearchMode ? true : activeTab === "pending"}
            showCategoryLabel={isSearchMode}
            onExit={openExitDialog}
            onDeleteDepleted={handleDeleteDepleted}
          />
        )}

        {/* Results count */}
        {!isLoading && (isSearchMode ? filteredSearchResults : tabTires).length > 0 && (
          <div className="mt-3 text-center text-xs text-[#A3A3A3]">
            {(isSearchMode ? filteredSearchResults : tabTires).length} ligne{(isSearchMode ? filteredSearchResults : tabTires).length > 1 ? 's' : ''} • {(isSearchMode ? filteredSearchResults : tabTires).reduce((sum, g) => sum + g.total_quantity, 0)} pneu{(isSearchMode ? filteredSearchResults : tabTires).reduce((sum, g) => sum + g.total_quantity, 0) > 1 ? 's' : ''}
          </div>
        )}
      </main>

      {/* Stock Exit Dialog */}
      <Dialog open={!!exitTire} onOpenChange={(open) => { if (!open) { setExitTire(null); setExitQty(1); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="exit-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minus className="w-4 h-4" />
              Sortir du stock
            </DialogTitle>
            <DialogDescription className="sr-only">Retirer des pneus du stock</DialogDescription>
          </DialogHeader>
          <div className="p-6 pt-4">
            {exitTire && (
              <div className="space-y-4">
                <div className="bg-[#F3F4F6] p-3 rounded-md">
                  <div className="font-mono font-bold text-[#0A0A0A]">{exitTire.dimensions} {exitTire.load_speed}</div>
                  <div className="text-sm text-[#525252]">{exitTire.brand} {exitTire.model}</div>
                  <div className="text-sm text-[#525252] mt-1">Stock actuel : <strong>{exitTire.total_quantity}</strong></div>
                </div>
                <div>
                  <Label className="input-label">Combien de pneus souhaitez-vous sortir ?</Label>
                  <Select value={exitQty.toString()} onValueChange={(v) => setExitQty(parseInt(v))}>
                    <SelectTrigger className="input-field" data-testid="exit-qty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: exitTire.total_quantity }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {exitQty === exitTire.total_quantity && (
                  <p className="text-xs text-[#DC2626] font-medium">
                    Ce pneu sera retiré du stock actif (quantité = 0).
                  </p>
                )}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setExitTire(null); setExitQty(1); }}>
                    Annuler
                  </Button>
                  <Button
                    className="bg-[#DC2626] text-white hover:bg-[#B91C1C]"
                    onClick={handleStockExit}
                    disabled={isExiting}
                    data-testid="confirm-exit-btn"
                  >
                    {isExiting ? "En cours..." : `Sortir ${exitQty} pneu${exitQty > 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Export Selection Modal */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="pdf-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-4 h-4" />Export PDF</DialogTitle>
            <DialogDescription className="sr-only">Sélection des catégories pour l'export PDF</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-[#E5E7EB]">
              <Checkbox
                checked={allPdfChecked}
                onCheckedChange={(v) => {
                  const val = !!v;
                  setPdfCategories(pdfOptions.reduce((acc, o) => ({ ...acc, [o.id]: val }), {}));
                }}
              />
              <span className="text-sm font-semibold text-[#0A0A0A]">Tout sélectionner</span>
            </label>
            {pdfOptions.map(opt => (
              <label key={opt.id} className="flex items-center gap-2 cursor-pointer" data-testid={`pdf-cat-${opt.id}`}>
                <Checkbox
                  checked={pdfCategories[opt.id]}
                  onCheckedChange={(v) => setPdfCategories(prev => ({ ...prev, [opt.id]: !!v }))}
                />
                <span className={`text-sm ${opt.id.startsWith("depleted") ? "text-[#DC2626]" : "text-[#0A0A0A]"}`}>{opt.label}</span>
              </label>
            ))}
            <div className="flex gap-2 pt-3 border-t border-[#E5E7EB]">
              <Button variant="outline" className="flex-1" onClick={() => setShowPdfModal(false)}>Annuler</Button>
              <Button className="btn-primary flex-1" onClick={handleExportPDF} data-testid="pdf-generate-btn">Générer PDF</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Tire Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] flex flex-col" data-testid="add-tire-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Ajouter un pneu</DialogTitle>
            <DialogDescription className="sr-only">Formulaire d'ajout de pneu</DialogDescription>
          </DialogHeader>
          <div className="px-6 pt-4 pb-6 overflow-y-auto flex-1 min-h-0">
            <AddTireForm 
              onClose={() => setShowAddForm(false)} 
              onSuccess={refreshData}
              formValues={formValues}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tire Dialog */}
      <Dialog open={!!editingTire} onOpenChange={(open) => !open && setEditingTire(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] flex flex-col" data-testid="edit-tire-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" />
              Modifier le pneu
            </DialogTitle>
            <DialogDescription className="sr-only">Formulaire de modification de pneu</DialogDescription>
          </DialogHeader>
          <div className="px-6 pt-4 pb-6 overflow-y-auto flex-1 min-h-0">
            {editingTire && (
              <EditTireForm 
                tire={editingTire}
                onClose={() => setEditingTire(null)} 
                onSuccess={refreshData}
                formValues={formValues}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Panel Dialog */}
      <Dialog open={showUserManagement} onOpenChange={setShowUserManagement}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-panel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Administration
            </DialogTitle>
            <DialogDescription className="sr-only">Panneau d'administration</DialogDescription>
          </DialogHeader>
          <div className="p-4 sm:p-6 pt-3 sm:pt-4">
            <AdminPanel onClose={() => setShowUserManagement(false)} initialTab={adminInitialTab} onImportSuccess={refreshData} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
